import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, Play, Save, Loader2, ScanSearch } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge, RiskBadge } from "@/components/common/StatusBadge";
import ScreeningPanel from "@/components/screening/ScreeningPanel";
import PaisesGafi from "@/components/screening/PaisesGafi";
import RiesgoEBR from "@/components/riesgo/RiesgoEBR";
import MonitoreoPerfil from "@/components/pld/MonitoreoPerfil";
import MonitoreoEfectivo from "@/components/pld/MonitoreoEfectivo";
import BandejaOC from "@/components/pld/BandejaOC";
import BandejaDenuncias from "@/components/pld/BandejaDenuncias";
import ReportesUif from "@/components/pld/ReportesUif";
import TableroPui from "@/components/pld/TableroPui";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { listResource, updateResource, api } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExportExcelButton } from "@/components/common/ExportExcelButton";

const SUBMODULOS = [
  "Perfil transaccional", "Listas (PEP / bloqueados)", "Operaciones relevantes", "Operaciones inusuales",
  "Operaciones internas preocupantes", "Oficial de Cumplimiento", "Comité de Comunicación y Control",
  "Capacitación", "Auditoría PLD", "Bitácora PLD",
];

const ALERT_COLS = [
  { key: "folio", label: "Folio" },
  { key: "fecha", label: "Fecha", kind: "date" },
  { key: "cliente", label: "Cliente" },
  { key: "regla", label: "Regla" },
  { key: "nivel_riesgo", label: "Riesgo", kind: "risk" },
  { key: "estatus", label: "Estatus", kind: "badge" },
];

export default function Pld() {
  const { user, giro, hasArea } = useAuth();
  // feb-2026 · iter 07-25 · Ocultamiento por giro de contenido CNBV-only. Una inmobiliaria
  // (LFPIORPI Art. 17, supervisor SAT-UIF) NO reporta al SITI CNBV ni consulta PUI/RENAPO
  // ni presenta Paquete de Auditoría CNBV ni tiene Comité de Comunicación y Control (Art. 8
  // DCG SOFOM). Este flag solo controla VISIBILIDAD; la lógica de los componentes queda intacta.
  // Cuando se agreguen otras actividades vulnerables (joyería, vehículos, arte...) el mismo
  // `giro !== "sofom"` las excluirá por default.
  const esSofom = giro === "sofom";
  const [alertas, setAlertas] = useState([]);
  const [matriz, setMatriz] = useState([]);
  const [casos, setCasos] = useState([]);
  const [reportes, setReportes] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [evaluando, setEvaluando] = useState(false);
  const [barriendo, setBarriendo] = useState(false);
  // oct-2026 · El barrido corre en segundo plano: aquí solo se sigue su avance.
  const [job, setJob] = useState(null);
  // Señal de recarga para la tarjeta del barrido, que vive dentro de ScreeningPanel.
  const [barridoRefresh, setBarridoRefresh] = useState(0);
  const puedeBarrer = ["oficial_cumplimiento", "admin_entidad"].includes(user?.role);
  const barridoEnCurso = job?.estado === "en_curso";

  const puedeCerrar = ["super_admin", "admin_entidad", "oficial_cumplimiento"].includes(user?.role);

  const loadAll = async () => {
    setAlertas(await listResource("alertas"));
    setMatriz(await listResource("matriz_riesgo"));
    setCasos(await listResource("casos"));
    setReportes(await listResource("reportes_cnbv"));
  };
  useEffect(() => { loadAll(); }, []);

  const evaluar = async () => {
    setEvaluando(true);
    try {
      const { data } = await api.post("/alertas/evaluar");
      toast.success(`Reglas de alertas evaluadas: ${data.generadas} nueva(s) alerta(s)`);
      setAlertas(await listResource("alertas"));
    } catch (e) {
      toast.error("No se pudieron evaluar las reglas");
    } finally {
      setEvaluando(false);
    }
  };

  const correrBarrido = async () => {
    setBarriendo(true);
    try {
      const { data } = await api.post("/pld/barrido-listas/correr");
      setJob(data);
      if (data.ya_en_curso) {
        toast.info("Ya hay un barrido en curso para tu entidad", {
          description: data.total_sujetos
            ? `Avance: ${data.cotejadas || 0} de ${data.total_sujetos} sujeto(s).`
            : "Arrancando el cotejo; el avance aparece en el botón.",
        });
      } else {
        toast.success("Barrido en curso", {
          description: "Corre en segundo plano: puedes seguir trabajando. Te avisamos al terminar.",
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo correr el barrido de listas");
    } finally {
      setBarriendo(false);
    }
  };

  // Avance del barrido: el cotejo corre en segundo plano (hilo aparte en el backend), así
  // que la pantalla lo sigue por `GET /pld/barrido-listas/job` hasta que termina.
  const leerJob = useCallback(async () => {
    try {
      const { data } = await api.get("/pld/barrido-listas/job");
      return data?.job || null;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!esSofom || !puedeBarrer) return undefined;
    let activo = true;
    leerJob().then((j) => { if (activo) setJob(j); });
    return () => { activo = false; };
  }, [esSofom, puedeBarrer, leerJob]);

  useEffect(() => {
    if (job?.estado !== "en_curso") return undefined;
    let activo = true;
    const timer = setInterval(async () => {
      const j = await leerJob();
      if (!activo || !j) return;
      setJob(j);
      if (j.estado === "en_curso") return;
      if (j.estado === "terminado") {
        const r = j.resultado || {};
        const detalle = !r.total_coincidencias
          ? "sin coincidencias"
          : `${r.total_coincidencias} coincidencia(s), ${r.coincidencias_nuevas} nueva(s)`;
        toast.success(
          `Barrido terminado · ${r.folio}: ${r.total_sujetos} sujeto(s) cotejado(s) — ${detalle}.`,
          { description: "Constancia sellada disponible en Listas / Screening." });
      } else {
        toast.error(j.error || "El barrido no terminó correctamente. Vuelve a intentarlo.");
      }
      setBarridoRefresh((v) => v + 1);
      listResource("alertas").then(setAlertas).catch(() => {});
    }, 2500);
    return () => { activo = false; clearInterval(timer); };
  }, [job?.estado, leerJob]);

  const openAlert = (row) => { setSel(row); setDraft({ estatus: row.estatus, determinacion: row.determinacion || "", comentarios: row.comentarios || "" }); };

  const saveAlert = async () => {
    if (draft.estatus === "Cerrada" && !draft.determinacion && !puedeCerrar) {
      toast.error("Solo el Oficial de Cumplimiento puede cerrar alertas con determinación.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...draft };
      if (draft.estatus === "Cerrada") body.fecha_cierre = new Date().toISOString().slice(0, 10);
      await updateResource("alertas", sel.id, body);
      toast.success("Alerta actualizada");
      setSel(null);
      setAlertas(await listResource("alertas"));
    } catch (e) {
      toast.error("No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={esSofom ? "PLD / CNBV" : "PLD"}
        subtitle={esSofom
          ? "Prevención de Lavado de Dinero — matriz de riesgo, alertas y casos"
          : "Prevención de Lavado de Dinero — LFPIORPI · matriz de riesgo, alertas y casos"}>
        {esSofom && (
          <Button data-testid="correr-barrido-btn" onClick={correrBarrido}
                  disabled={barriendo || barridoEnCurso || !puedeBarrer}
                  title={puedeBarrer
                    ? "Coteja clientes, prospectos, beneficiarios, socios y acreedores contra tus listas y el Centro de Listas vigente"
                    : "Solo el Oficial de Cumplimiento o la Administración de la entidad puede correr el barrido"}
                  className="bg-accent hover:bg-forest-700">
            {barriendo || barridoEnCurso
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <ScanSearch className="mr-2 h-4 w-4" />}
            <span data-testid="barrido-estado-texto">
              {barridoEnCurso
                ? (job?.total_sujetos
                    ? `Barrido en curso · ${(job.cotejadas || 0).toLocaleString("es-MX")} de ${job.total_sujetos.toLocaleString("es-MX")}`
                    : "Barrido en curso")
                : "Correr barrido"}
            </span>
          </Button>
        )}
      </PageHeader>

      <div className="p-6 md:p-8">
        <Tabs defaultValue="alertas">
          <TabsList className="mb-4 h-auto flex-wrap">
            <TabsTrigger value="alertas" data-testid="tab-alertas">Alertas</TabsTrigger>
            <TabsTrigger value="screening" data-testid="tab-screening">Listas / Screening</TabsTrigger>
            <TabsTrigger value="perfil" data-testid="tab-perfil-transaccional">Perfil transaccional</TabsTrigger>
            <TabsTrigger value="efectivo" data-testid="tab-efectivo">Efectivo / Relevantes</TabsTrigger>
            <TabsTrigger value="dictamen" data-testid="tab-dictamen">Dictamen OC</TabsTrigger>
            {["oficial_cumplimiento", "admin_entidad"].includes(user?.role) && (
              <TabsTrigger value="denuncias" data-testid="tab-denuncias">Canal de denuncia</TabsTrigger>
            )}
            <TabsTrigger value="matriz" data-testid="tab-matriz">Matriz de riesgo</TabsTrigger>
            <TabsTrigger value="casos" data-testid="tab-casos">Gestión de casos</TabsTrigger>
            {esSofom && (
              <TabsTrigger value="reportes-uif" data-testid="tab-reportes-uif">Reportes UIF</TabsTrigger>
            )}
            {esSofom && hasArea && hasArea("cumplimiento") && (
              <TabsTrigger value="pui" data-testid="tab-pui">PUI / RENAPO</TabsTrigger>
            )}
            {esSofom && (
              <TabsTrigger value="reportes" data-testid="tab-reportes-cnbv">Reportes CNBV</TabsTrigger>
            )}
            <TabsTrigger value="estructura" data-testid="tab-estructura">Estructura PLD</TabsTrigger>
          </TabsList>

          <TabsContent value="alertas">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <Button data-testid="evaluar-reglas-btn" size="sm" variant="outline"
                      onClick={evaluar} disabled={evaluando}
                      title="Revisa clientes y expedientes y levanta alertas por PEP, expediente incompleto y bandera manual de listas">
                {evaluando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Evaluar reglas de alertas
              </Button>
              {hasArea && hasArea("cumplimiento") && (
                <ExportExcelButton endpoint="/exportar/pld/alertas" filename="alertas_pld.xlsx" testid="export-alertas-btn" />
              )}
            </div>
            <DataTable testid="table-alertas" columns={ALERT_COLS} rows={alertas} onRowClick={openAlert}
              emptyLabel="Sin alertas. Evalúa las reglas de alertas o corre el barrido de listas." />
          </TabsContent>

          <TabsContent value="screening">
            <PaisesGafi />
            <ScreeningPanel barridoRefresh={barridoRefresh} barridoJob={job} />
          </TabsContent>

          <TabsContent value="perfil">
            <MonitoreoPerfil />
          </TabsContent>

          <TabsContent value="efectivo">
            <MonitoreoEfectivo />
          </TabsContent>

          <TabsContent value="dictamen">
            <BandejaOC />
          </TabsContent>

          <TabsContent value="denuncias">
            <BandejaDenuncias />
          </TabsContent>

          <TabsContent value="matriz">
            <RiesgoEBR />
            <div className="mt-6">
              <p className="mb-2 text-sm font-medium text-slate-700">Variables base (matriz_riesgo)</p>
              <DataTable testid="table-matriz" rows={matriz}
                columns={[
                  { key: "variable", label: "Variable" },
                  { key: "ponderacion", label: "Ponderación" },
                  { key: "puntaje_bajo", label: "Bajo" },
                  { key: "puntaje_medio", label: "Medio" },
                  { key: "puntaje_alto", label: "Alto" },
                  { key: "activa", label: "Activa", kind: "bool" },
                ]} emptyLabel="Sin variables configuradas" />
              <p className="mt-3 text-xs text-slate-400">Insumo de sub-variables de la metodología EBR (resultado: bajo / medio / alto).</p>
            </div>
          </TabsContent>

          <TabsContent value="casos">
            {hasArea && hasArea("cumplimiento") && (
              <div className="mb-3 flex justify-end">
                <ExportExcelButton endpoint="/exportar/pld/casos" filename="casos_pld.xlsx" testid="export-casos-btn" />
              </div>
            )}
            <DataTable testid="table-casos-pld" rows={casos}
              columns={[
                { key: "folio", label: "Folio" },
                { key: "tipo_caso", label: "Tipo", kind: "badge" },
                { key: "cliente", label: "Cliente" },
                { key: "nivel_riesgo", label: "Riesgo", kind: "risk" },
                { key: "estatus", label: "Estatus", kind: "badge" },
                { key: "responsable", label: "Responsable" },
              ]} emptyLabel="Sin casos" />
          </TabsContent>

          <TabsContent value="reportes-uif">
            <ReportesUif />
          </TabsContent>

          <TabsContent value="pui">
            <TableroPui />
          </TabsContent>

          <TabsContent value="reportes">
            <p className="mb-3 text-xs text-slate-400">Histórico de solo lectura. La generación, descarga y transmisión de reportes se realiza en la pestaña “Reportes UIF”.</p>
            <DataTable testid="table-reportes-cnbv" rows={reportes}
              columns={[
                { key: "tipo", label: "Tipo de reporte" },
                { key: "periodo", label: "Periodo" },
                { key: "autoridad", label: "Autoridad", kind: "badge" },
                { key: "estatus", label: "Estatus", kind: "badge" },
                { key: "fecha_limite", label: "Fecha límite", kind: "date" },
                { key: "acuse", label: "Acuse" },
              ]} emptyLabel="Sin reportes CNBV" />
          </TabsContent>

          <TabsContent value="estructura">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {SUBMODULOS
                // feb-2026 · Comité de Comunicación y Control es figura del Art. 8 DCG SOFOM
                // (régimen CNBV). No aplica a actividades vulnerables LFPIORPI.
                .filter((s) => esSofom || s !== "Comité de Comunicación y Control")
                .map((s) => (
                  <div key={s} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10"><ShieldAlert className="h-4 w-4 text-primary" /></div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{s}</p>
                      <p className="text-xs text-slate-400">Submódulo PLD configurable</p>
                    </div>
                  </div>
                ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Alerta {sel.folio}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-400">Cliente</span><p className="font-medium">{sel.cliente}</p></div>
                  <div><span className="text-slate-400">Fecha</span><p className="font-medium">{fdate(sel.fecha)}</p></div>
                  <div className="col-span-2"><span className="text-slate-400">Regla</span><p className="font-medium">{sel.regla}</p></div>
                  <div><span className="text-slate-400">Riesgo</span><div className="mt-1"><RiskBadge value={sel.nivel_riesgo} /></div></div>
                  <div><span className="text-slate-400">Origen</span><div className="mt-1"><StatusBadge value={sel.origen} /></div></div>
                </div>
                <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">{sel.descripcion}</p>
                <div>
                  <Label className="mb-1 block text-xs">Estatus</Label>
                  <Select value={draft.estatus} onValueChange={(v) => setDraft((p) => ({ ...p, estatus: v }))}>
                    <SelectTrigger data-testid="alert-estatus"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Abierta", "En análisis", "Escalada", "Reportada", "Cerrada"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Determinación / Comentario</Label>
                  <Textarea data-testid="alert-determinacion" rows={4} value={draft.determinacion}
                    onChange={(e) => setDraft((p) => ({ ...p, determinacion: e.target.value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSel(null)}>Cerrar</Button>
                <Button data-testid="save-alert-btn" onClick={saveAlert} disabled={saving} className="bg-primary hover:bg-sidebar-hover">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Guardar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
